/**
 * F44 (Wave 2D) — engineering-authored AR-aging visual.
 *
 * @bravemove/ui-web has no `AgingBar` primitive and `Badge` has no aging-bucket
 * tone (current/30+/60+/90+). This composition renders the 5 AR-aging buckets
 * as a single horizontal stacked bar (proportional to each bucket's amount)
 * with a colour-coded legend + per-bucket amount via <CurrencyDisplay>. The
 * tone darkens toward overdue (current=success → 90+=danger).
 *
 * TODO(F44 design-polish): promote AgingBar + PercentDelta to @bravemove/ui-web
 * primitives in the sprint-end design-polish wave.
 */
import { CurrencyDisplay, cn } from '@bravemove/ui-web';
import type { ArAgingBucket } from '@bravemove/types';

export interface AgingBarSegment {
  readonly bucket: ArAgingBucket;
  readonly amountMinor: number;
  readonly statementCount: number;
}

export interface AgingBarProps {
  readonly segments: readonly AgingBarSegment[];
  /** Per-bucket display label (i18n) keyed by bucket. */
  readonly bucketLabel: (bucket: ArAgingBucket) => string;
  /** Accessible label for the bar (i18n — supplied by the caller). */
  readonly ariaLabel: string;
  readonly locale?: string;
  readonly className?: string;
}

/** Bucket → token-driven background class (success → danger as overdue grows). */
const BUCKET_BG: Record<ArAgingBucket, string> = {
  current: 'bg-success',
  '1_30': 'bg-info',
  '31_60': 'bg-warning',
  '61_90': 'bg-secondary',
  '90_plus': 'bg-danger',
};
const BUCKET_DOT: Record<ArAgingBucket, string> = {
  current: 'bg-success',
  '1_30': 'bg-info',
  '31_60': 'bg-warning',
  '61_90': 'bg-secondary',
  '90_plus': 'bg-danger',
};

export function AgingBar({
  segments,
  bucketLabel,
  ariaLabel,
  locale = 'en-US',
  className,
}: AgingBarProps) {
  const total = segments.reduce((sum, s) => sum + s.amountMinor, 0);
  return (
    <div className={cn('flex flex-col gap-3', className)} data-testid="finance-aging-bar">
      <div
        className="flex h-3 w-full overflow-hidden rounded-full bg-surface-2"
        role="img"
        aria-label={ariaLabel}
      >
        {segments.map((s) =>
          total > 0 && s.amountMinor > 0 ? (
            <div
              key={s.bucket}
              className={BUCKET_BG[s.bucket]}
              style={{ width: `${(s.amountMinor / total) * 100}%` }}
              data-testid={`finance-aging-seg-${s.bucket}`}
            />
          ) : null,
        )}
      </div>
      <ul className="flex flex-wrap gap-x-6 gap-y-2">
        {segments.map((s) => (
          <li
            key={s.bucket}
            className="flex items-center gap-2"
            data-testid={`finance-aging-legend-${s.bucket}`}
          >
            <span className={cn('inline-block size-2.5 rounded-full', BUCKET_DOT[s.bucket])} />
            <span className="text-[12px] text-ink-2">{bucketLabel(s.bucket)}</span>
            <CurrencyDisplay amount={s.amountMinor} currency="USD" locale={locale} size="sm" />
          </li>
        ))}
      </ul>
    </div>
  );
}
